import {
  AdminClientDetail,
  AdminClientRecord,
  AdminDashboard,
  AdminPaymentChartPoint,
  AdminPaymentRecord,
  AdminSettings,
  AdminSubscriptionDetails,
  AdminUserRecord,
  CommissionRule
} from '@app/core/models';
import { AdminPartner, AdminPartnerDetail } from '@app/core/models/partner.models';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isEnvelope = <T>(value: unknown): value is { success?: boolean; data?: T; error?: unknown } =>
  isRecord(value) && ('data' in value || 'success' in value || 'error' in value);

const readArray = <T>(value: unknown): T[] | null => {
  if (Array.isArray(value)) {
    return value as T[];
  }

  if (!isRecord(value)) {
    return null;
  }

  const arrayKeys = [
    'items',
    'results',
    'rows',
    'clients',
    'partners',
    'users',
    'payments',
    'rules',
    'records',
    'points',
    'paymentActivity'
  ] as const;

  for (const key of arrayKeys) {
    if (Array.isArray(value[key])) {
      return value[key] as T[];
    }
  }

  return null;
};

const readString = (record: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

const readNumber = (record: Record<string, unknown>, keys: string[]): number => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }

  return 0;
};

const normalizeEnum = (value: string): string =>
  value.trim().replace(/[\s-]+/g, '_').toUpperCase();

const utcDate = (value: string): string => value && !/(Z|[+-]\d{2}:\d{2})$/i.test(value) ? `${value}Z` : value;

const normalizeClientRecord = (value: unknown): AdminClientRecord => {
  const record = isRecord(value) ? value : {};
  const partnerRecord = isRecord(record['partner']) ? record['partner'] : null;
  const subscriptionRecord = isRecord(record['subscription']) ? record['subscription'] : null;

  const partnerName =
    readString(record, ['partnerName', 'partnerDisplayName', 'assignedPartnerName', 'referrerName']) ||
    (partnerRecord ? readString(partnerRecord, ['displayName', 'name', 'companyName']) : '');

  const subscriptionStatus =
    readString(record, ['subscriptionStatus', 'billingStatus']) ||
    (subscriptionRecord ? readString(subscriptionRecord, ['status']) : '');

  return {
    id: readString(record, ['id', 'clientId', 'ownerAccountId', 'ownerId']),
    partnerUserId: readString(record, ['partnerUserId', 'partnerId', 'referrerPartnerId']),
    partnerName,
    clientName: readString(record, ['clientName', 'contactName', 'ownerName', 'name', 'fullName', 'displayName']),
    companyName: readString(record, ['companyName', 'businessName', 'accountName', 'ownerCompanyName']),
    contactEmail: readString(record, ['contactEmail', 'email', 'ownerEmail']),
    contactPhone: readString(record, ['contactPhone', 'phone', 'ownerPhone', 'phoneNumber']),
    status: (normalizeEnum(readString(record, ['status', 'clientStatus', 'accountStatus'])) || 'LEAD') as AdminClientRecord['status'],
    onboardingStage: (normalizeEnum(readString(record, ['onboardingStage', 'onboardingStatus', 'stage'])) || 'NEW') as AdminClientRecord['onboardingStage'],
    subscriptionStatus: (normalizeEnum(subscriptionStatus) || 'NONE') as AdminClientRecord['subscriptionStatus'],
    propertyCount: readNumber(record, ['propertyCount', 'propertiesCount', 'activePropertyCount']),
    referredRevenue: readNumber(record, ['referredRevenue', 'revenue', 'totalRevenue']),
    commissionRate: readNumber(record, ['commissionRate', 'commission', 'commissionPercentage']),
    lastActivityAt: utcDate(readString(record, ['lastActivityAtUtc', 'lastActivityAt', 'updatedAtUtc', 'updatedAt', 'lastPaymentAt', 'lastLoginAtUtc', 'lastLoginAt', 'createdAtUtc', 'createdAt'])),
    joinedAt: utcDate(readString(record, ['joinedAt', 'createdAtUtc', 'createdAt'])) || undefined,
    stripeCustomerId: readString(record, ['stripeCustomerId', 'customerId']) || null,
    stripeSubscriptionId: readString(record, ['stripeSubscriptionId', 'subscriptionId']) || null,
    ownerUserId: readString(record, ['ownerUserId', 'userId']) || undefined
  };
};

const normalizeSubscriptionDetails = (value: unknown): AdminSubscriptionDetails | null => {
  if (!isRecord(value)) {
    return null;
  }

  return {
    ...(value as AdminSubscriptionDetails),
    currentPeriodStart: readString(value, ['currentPeriodStart', 'currentPeriodStartUtc']) || null,
    currentPeriodEnd: readString(value, ['currentPeriodEnd', 'currentPeriodEndUtc']) || null,
    nextPaymentDate:
      readString(value, ['nextPaymentDate', 'nextPaymentDateUtc', 'currentPeriodEnd', 'currentPeriodEndUtc']) || null
  };
};

const normalizePaymentRecord = (value: unknown): AdminPaymentRecord => {
  const record = isRecord(value) ? value : {};
  const createdAt = readString(record, ['date', 'createdAt', 'createdAtUtc']);

  return {
    ...(record as unknown as AdminPaymentRecord),
    id: readString(record, ['id', 'paymentActivityId']),
    clientId: readString(record, ['clientId']) || null,
    clientName: readString(record, ['clientName']) || null,
    clientEmail: readString(record, ['clientEmail']) || null,
    date: createdAt,
    createdAt: createdAt || null,
    paidAt: readString(record, ['paidAt', 'paidAtUtc']) || null,
    amount: readNumber(record, ['amount']),
    refundedAmount: readNumber(record, ['refundedAmount', 'amountRefunded']),
    currency: readString(record, ['currency']) || 'USD',
    status: readString(record, ['status']),
    type: readString(record, ['type', 'paymentType'])
  };
};

const normalizePaymentArray = (value: unknown): AdminPaymentRecord[] =>
  (readArray<unknown>(value) ?? []).map((entry) => normalizePaymentRecord(entry));

const normalizeClientDetail = (value: unknown): AdminClientDetail => {
  const record = isRecord(value) ? value : {};
  const normalized = normalizeClientRecord(record);

  return {
    ...normalized,
    profileSummary: isRecord(record['profileSummary']) ? record['profileSummary'] : undefined,
    subscription: normalizeSubscriptionDetails(record['subscription']),
    payments: normalizePaymentArray(record['payments']),
    previousPayments: normalizePaymentArray(record['previousPayments']),
    pendingPayments: normalizePaymentArray(record['pendingPayments'])
  };
};

export const unwrapAdminData = <T>(response: unknown): T => {
  if (isEnvelope<T>(response) && response.data !== undefined) {
    return response.data;
  }

  return response as T;
};

export const unwrapAdminArray = <T>(response: unknown): T[] => {
  if (isEnvelope<unknown>(response)) {
    const wrappedArray = readArray<T>(response.data);
    if (wrappedArray) {
      return wrappedArray;
    }

    if (Array.isArray(response.data)) {
      return response.data as T[];
    }
  }

  return readArray<T>(response) ?? [];
};

const normalizeUserRecord = (value: unknown): AdminUserRecord => {
  const record = isRecord(value) ? value : {};
  return {
    ...(record as unknown as AdminUserRecord),
    id: readString(record, ['id', 'userId']),
    fullName: readString(record, ['fullName', 'displayName']) || [record['firstName'], record['lastName']].filter(Boolean).join(' '),
    email: readString(record, ['email']),
    role: (readString(record, ['role']).toLowerCase() || 'admin') as AdminUserRecord['role'],
    accessStatus: (normalizeEnum(readString(record, ['accessStatus', 'status'])) || 'ACTIVE') as AdminUserRecord['accessStatus'],
    createdAt: utcDate(readString(record, ['createdAtUtc', 'createdAt'])),
    lastLoginAt: utcDate(readString(record, ['lastLoginAtUtc', 'lastLoginAt']))
  };
};

export const unwrapAdminDashboard = (response: unknown): AdminDashboard => {
  const record = unwrapAdminData<Record<string, unknown>>(response);
  return {
    ...(record as unknown as AdminDashboard),
    totalClients: readNumber(record, ['totalClients']),
    totalUsers: readNumber(record, ['totalUsers', 'activeAdmins']),
    activePartners: readNumber(record, ['activePartners']),
    activeProperties: readNumber(record, ['activeProperties']),
    clientPipeline: readArray<AdminDashboard['clientPipeline'][number]>(record['clientPipeline']) ?? [],
    recentClients: (readArray<unknown>(record['recentClients']) ?? []).map(normalizeClientRecord),
    recentUsers: (readArray<unknown>(record['recentUsers']) ?? []).map(normalizeUserRecord),
    topPartners: readArray<AdminDashboard['topPartners'][number]>(record['topPartners']) ?? []
  };
};

export const unwrapAdminClients = (response: unknown): AdminClientRecord[] =>
  unwrapAdminArray<unknown>(response).map((entry) => normalizeClientRecord(entry));

export const unwrapAdminClientDetail = (response: unknown): AdminClientDetail =>
  normalizeClientDetail(unwrapAdminData<unknown>(response));

export const unwrapAdminUsers = (response: unknown): AdminUserRecord[] =>
  unwrapAdminArray<unknown>(response).map(normalizeUserRecord);

export const unwrapAdminSettings = (response: unknown): AdminSettings =>
  unwrapAdminData<AdminSettings>(response);

export const unwrapAdminPayments = (response: unknown): AdminPaymentRecord[] =>
  unwrapAdminArray<unknown>(response).map((entry) => normalizePaymentRecord(entry));

export const unwrapAdminPaymentChart = (response: unknown): AdminPaymentChartPoint[] =>
  unwrapAdminArray<Record<string, unknown>>(response).map(point => ({
    label: readString(point, ['label']),
    amount: readNumber(point, ['amount', 'totalAmount']),
    count: readNumber(point, ['count', 'paymentCount'])
  }));

export const unwrapAdminPartners = (response: unknown): AdminPartner[] =>
  unwrapAdminArray<AdminPartner>(response);

export const unwrapAdminPartnerDetail = (response: unknown): AdminPartnerDetail => {
  const raw = unwrapAdminData<unknown>(response);
  const record = isRecord(raw) ? raw : {};
  return {
    ...(record as unknown as AdminPartnerDetail),
    totalClients: readNumber(record, ['totalClients']),
    activeClients: readNumber(record, ['activeClients']),
    invitesSent: readNumber(record, ['invitesSent']),
    conversions: readNumber(record, ['conversions']),
    referredRevenue: readNumber(record, ['referredRevenue']),
    commissionsPending: readNumber(record, ['commissionsPending']),
    commissionsPaid: readNumber(record, ['commissionsPaid']),
    payoutsPending: readNumber(record, ['payoutsPending']),
    clients: (readArray<unknown>(record['clients']) ?? []).map(value => {
      const client = isRecord(value) ? value : {};
      return {
        id: readString(client, ['partnerClientId', 'id', 'clientId']),
        clientName: readString(client, ['clientName', 'name']),
        email: readString(client, ['email', 'contactEmail']),
        phone: readString(client, ['phone', 'contactPhone']),
        commissionPaid: readNumber(client, ['commissionPaid']),
        status: normalizeEnum(readString(client, ['status'])) as AdminPartnerDetail['clients'][number]['status'],
        subscriptionStatus: normalizeEnum(readString(client, ['subscriptionStatus'])) as AdminPartnerDetail['clients'][number]['subscriptionStatus'],
        joinedAt: utcDate(readString(client, ['joinedAt', 'joinedDate', 'createdAtUtc'])),
        stripeCustomerId: readString(client, ['stripeCustomerId']) || null
      };
    })
  };
};

export const unwrapCommissionRule = (response: unknown): CommissionRule => {
  const rule = unwrapAdminData<CommissionRule & {
    type?: string;
    active?: boolean;
    createdAtUtc?: string;
    updatedAtUtc?: string;
    effectiveStartUtc?: string | null;
    effectiveEndUtc?: string | null;
  }>(response);
  return {
    ...rule,
    commissionType: (rule.type ?? rule.commissionType)?.toLowerCase() === 'fixed' ? 'FIXED' : 'PERCENTAGE',
    value: rule.value ?? rule.defaultRate ?? 0,
    isActive: rule.active ?? rule.isActive,
    createdAt: rule.createdAtUtc ?? rule.createdAt,
    updatedAt: rule.updatedAtUtc ?? rule.updatedAt,
    effectiveStartDate: rule.effectiveStartUtc ?? rule.effectiveStartDate ?? null,
    effectiveEndDate: rule.effectiveEndUtc ?? rule.effectiveEndDate ?? null
  };
};

export const unwrapCommissionRules = (response: unknown): CommissionRule[] =>
  unwrapAdminArray<unknown>(response).map(unwrapCommissionRule);
